import { all as veeValidateRules } from '@zaalbarxx/vee-validate-rules';
import { configure, defineRule } from '@zaalbarxx/vee-validate';
import VueTippy from 'vue-tippy';
import { Vue3Mq } from 'vue3-mq';
import Toasted from '@zaalbarxx/vue-toasted';
import { registerFilters } from '@/app/shared/filters';
import { installAllValidators } from '@/app/shared/validators';
import { RawTextPlugin } from '@fifa-bsm/bo-frontend-shared/core';
import { StaticData } from '@fifa-bsm/bo-frontend-shared/mixins';
import { createShowErrorToastInterceptor } from '@/app/core/interceptors/showErrorToast.interceptor';
import { createTransformErrorResponseInterceptor } from '@/app/core/interceptors/transformErrorResponse.interceptor';
import dayjs from 'dayjs';
import { LogPlugin } from '@fifa-bsm/bo-frontend-shared/core';
import { RollbarPlugin } from '@fifa-bsm/bo-frontend-shared/core';
import { AppInsightsPlugin } from '@fifa-bsm/bo-frontend-shared/core';
import { FeatureFlagsPlugin } from '@fifa-bsm/bo-frontend-shared/core';
import { FormUtilsPlugin } from '@fifa-bsm/bo-frontend-shared/core';
import { VueTippyDefaultSettings } from '@fifa-bsm/bo-frontend-shared/directives';
import dayjsAdvancedFormat from 'dayjs/plugin/advancedFormat';
import dayjsIsSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import dayjsUtc from 'dayjs/plugin/utc';
import dayjsTimezone from 'dayjs/plugin/timezone';
import dayjsMinMax from 'dayjs/plugin/minMax';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import { breakpoints } from '@/app/core/utils/media-queries';
import { localize } from '@zaalbarxx/vee-validate-i18n';
import { toSentence } from '@fifa-bsm/bo-frontend-shared/utils';
import { StorageUtilsPlugin } from '@fifa-bsm/bo-frontend-shared/core';
import { TemplateUtilsPlugin } from '@fifa-bsm/bo-frontend-shared/core';
import { RollbarService } from '@/app/core/services/rollbar.service';
import { isRollbarEnabled } from '@/app/core/utils/isRollbarEnabled';
import { APP_INSIGHTS_KEY, STORAGE_URL } from '@/app/core/common/config';
import { AppInsights } from '@/app/core/services/AppInsights.service';
import { t } from '@/i18n';
import VueTypes from 'vue-types';
import validationMessagesEn from '@/assets/locales/validation/en.json';

VueTypes.sensibleDefaults = false;

export const setupApp = ({ app }) => {
    registerPlugins(app);
    registerFilters(app);
    setupValidation(app);
    setupToastMessages(app);
    setupDayJs();
};

const registerPlugins = (app) => {
    app.use(RollbarPlugin, { enabled: isRollbarEnabled(), rollbarService: RollbarService });
    app.use(AppInsightsPlugin, {
        AppInsightsKey: APP_INSIGHTS_KEY,
        AppInsightsService: AppInsights,
    });
    app.use(FormUtilsPlugin, {
        enableLogging: import.meta.env.VITEST && import.meta.env.VITE_ENABLE_LOGGING_FORM_ERRORS,
        t,
    });
    app.use(VueTippy, VueTippyDefaultSettings);
    app.use(RawTextPlugin);
    app.use(StaticData);
    app.use(LogPlugin);
    app.use(FeatureFlagsPlugin);
    app.use(StorageUtilsPlugin, { storageUrl: STORAGE_URL });
    app.use(TemplateUtilsPlugin);
    app.use(Vue3Mq, {
        breakpoints,
    });
};

const setupToastMessages = (app) => {
    const toastOptions = {
        position: 'top-right',
        duration: 5000,
        theme: 'outline',
        action: {
            text: 'X',
            onClick: (e, toastObject) => {
                toastObject.goAway(0);
            },
        },
    };
    app.use(Toasted, toastOptions);
};

export const setupValidation = () => {
    setupValidators();
    const localizeMessage = localize({
        en: validationMessagesEn,
    });

    configure({
        generateMessage: (context) => {
            const ctx = { ...context };
            if (!ctx.label) {
                ctx.label = toSentence(ctx.name ?? '');
            }

            return localizeMessage(ctx);
        },
    });
};

export const setupValidators = () => {
    Object.keys(veeValidateRules).forEach((ruleKey) => {
        defineRule(ruleKey, veeValidateRules[ruleKey]);
    });

    installAllValidators();
};

export const registerInterceptors = (apiService, { toastService }) => {
    apiService.addInterceptor('response', createTransformErrorResponseInterceptor());
    apiService.addInterceptor('response', createShowErrorToastInterceptor(toastService));
};

const setupDayJs = () => {
    dayjs.extend(dayjsAdvancedFormat);
    dayjs.extend(dayjsIsSameOrAfter);
    dayjs.extend(dayjsUtc);
    dayjs.extend(dayjsTimezone);
    dayjs.extend(dayjsMinMax);
    dayjs.extend(duration);
    dayjs.extend(isBetween);
};
